import type { RefObject } from 'react';
import { Platform, Share, type View } from 'react-native';

/**
 * Capture an off-screen view to a PNG and open the OS share sheet with the
 * image attached - falling back to a text-only share when image capture is not
 * available in the current binary.
 *
 * WHY THE FALLBACK: `react-native-view-shot` is a NATIVE module. It is in
 * package.json so the next production BUILD ships true image capture, but an
 * OTA update (EAS Update) cannot add native code - until a build includes it,
 * `require('react-native-view-shot')` resolves to nothing at runtime. We
 * therefore lazy-require it inside a try/catch: when present we capture + share
 * the PNG; when absent (or on web, or if capture throws) we degrade gracefully
 * to the existing rich-text share so the button always does something useful.
 *
 * The text share is ALSO what reaches apps that can't take an image attachment,
 * so we always pass `message` alongside the `url`.
 */
export async function captureAndShare(options: {
  /** Ref to the off-screen branded card to rasterise. */
  viewRef: RefObject<View | null>;
  /** Plain-text caption shared with the image (and used alone on fallback). */
  message: string;
  /** OS share-sheet dialog title (Android). */
  dialogTitle?: string;
}): Promise<'image' | 'text' | 'cancelled'> {
  const { viewRef, message, dialogTitle } = options;

  // Web and missing-native-module both route here -> text only.
  const canCapture = Platform.OS === 'ios' || Platform.OS === 'android';

  if (canCapture && viewRef.current) {
    try {
      // Lazy + guarded: never let a missing native module crash the JS bundle.
      // eslint-disable-next-line @typescript-eslint/no-require-imports
      const viewShot = require('react-native-view-shot') as
        | { captureRef?: (v: unknown, o?: unknown) => Promise<string> }
        | undefined;
      const captureRef = viewShot?.captureRef;
      if (typeof captureRef === 'function') {
        const uri = await captureRef(viewRef.current, {
          format: 'png',
          quality: 1,
          result: 'tmpfile',
        });
        // RN Share takes a local file `url` for the image; `message` rides along
        // for apps (WhatsApp, X) that surface the caption next to the picture.
        await Share.share({ url: uri, message }, { dialogTitle });
        return 'image';
      }
    } catch {
      // Capture or share failed (module absent at runtime, user dismissed, or a
      // capture error) - fall through to the text share below.
    }
  }

  try {
    if (Platform.OS === 'web') {
      const nav =
        typeof navigator !== 'undefined'
          ? (navigator as Navigator & { share?: (d: { text?: string }) => Promise<void> })
          : undefined;
      if (nav?.share) await nav.share({ text: message });
      return 'text';
    }
    await Share.share({ message }, { dialogTitle });
    return 'text';
  } catch {
    return 'cancelled';
  }
}

/**
 * Whether true image-capture is available in this running binary. Used to label
 * the action honestly ("Share as image" vs the plain text share) and to decide
 * whether to mount the off-screen card at all.
 */
export function imageCaptureAvailable(): boolean {
  if (Platform.OS !== 'ios' && Platform.OS !== 'android') return false;
  try {
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const viewShot = require('react-native-view-shot') as { captureRef?: unknown } | undefined;
    return typeof viewShot?.captureRef === 'function';
  } catch {
    return false;
  }
}
